/**
 * SVG图标管理工具
 * 实现按需加载SVG图标，避免一次性加载所有图标
 * 同时支持预加载关键图标以提升用户体验
 */

class IconManager {
  constructor() {
    // 存储已加载的图标
    this.loadedIcons = new Set()

    // 存储正在加载的图标Promise
    this.loadingIcons = new Map()

    // 关键图标列表（如Tabbar图标）
    this.criticalIcons = ['home', 'category', 'cart', 'my']

    // 预加载关键图标
    this.preloadCriticalIcons()
  }

  /**
   * 动态导入SVG图标
   * @param {string} iconName 图标名称
   * @returns {Promise} 图标导入Promise
   */
  async loadIcon(iconName) {
    // 参数验证
    if (!iconName || typeof iconName !== 'string') {
      return Promise.reject(new Error('Invalid icon name'))
    }

    // 如果图标已加载，直接返回
    if (this.loadedIcons.has(iconName)) {
      return Promise.resolve()
    }

    // 如果图标正在加载，返回对应的Promise
    if (this.loadingIcons.has(iconName)) {
      return this.loadingIcons.get(iconName)
    }

    // 使用 require.context 来确保 webpack 能够正确解析所有图标
    try {
      // 创建一个映射来存储所有可能的图标导入
      const iconModules = {
        'shop-cart': () =>
          import('@/assets/icons/shop-cart.svg'),
        service: () => import('@/assets/icons/service.svg'),
        home: () => import('@/assets/icons/home.svg'),
        category: () =>
          import('@/assets/icons/category.svg'),
        cart: () => import('@/assets/icons/cart.svg'),
        my: () => import('@/assets/icons/my.svg')
        // 可以继续添加其他常用的图标
      }

      let modulePromise

      // 如果图标在预定义映射中，使用映射的导入函数
      if (iconModules[iconName]) {
        modulePromise = iconModules[iconName]()
      } else {
        // 否则使用原来的动态导入方式
        modulePromise = import(
          /* webpackInclude: /\.svg$/ */
          /* webpackChunkName: "icon-[request]" */
          `@/assets/icons/${iconName}.svg`
        )
      }

      const result = await modulePromise

      // 标记图标已加载
      this.loadedIcons.add(iconName)
      // 清除正在加载的状态
      this.loadingIcons.delete(iconName)

      return result
    } catch (error) {
      console.warn(
        `Failed to load icon: ${iconName}`,
        error
      )
      // 即使加载失败也要清除状态，避免重复尝试
      this.loadingIcons.delete(iconName)
      throw error
    }
  }

  /**
   * 预加载关键图标（如Tabbar图标）
   */
  preloadCriticalIcons() {
    // 使用Promise.allSettled确保即使某个图标加载失败也不会影响其他图标
    return Promise.allSettled(
      this.criticalIcons.map(iconName =>
        this.loadIcon(iconName)
      )
    ).then(results => {
      // 记录加载结果
      const successful = results.filter(
        result => result.status === 'fulfilled'
      ).length
      const failed = results.filter(
        result => result.status === 'rejected'
      ).length

      if (failed > 0) {
        console.warn(
          `Failed to preload ${failed} out of ${this.criticalIcons.length} critical icons`
        )
      } else {
        console.log(
          // `Successfully preloaded all ${successful} critical icons`
        )
      }

      return results
    })
  }

  /**
   * 批量预加载图标
   * @param {Array<string>} iconNames 图标名称数组
   */
  preloadIcons(iconNames) {
    if (!Array.isArray(iconNames)) {
      return Promise.reject(
        new Error('Icon names must be an array')
      )
    }

    return Promise.allSettled(
      iconNames.map(iconName => this.loadIcon(iconName))
    )
  }

  /**
   * 检查图标是否已加载
   * @param {string} iconName 图标名称
   * @returns {boolean} 是否已加载
   */
  isIconLoaded(iconName) {
    return this.loadedIcons.has(iconName)
  }

  /**
   * 获取已加载的图标列表
   * @returns {Array<string>} 已加载的图标名称数组
   */
  getLoadedIcons() {
    return Array.from(this.loadedIcons)
  }
}

// 创建单例实例
const iconManager = new IconManager()

// 导出实例和类
export default iconManager
export { IconManager }
